import React, { forwardRef, KeyboardEvent } from 'react';
import { clsx } from 'clsx';
import { IconType } from 'react-icons';
import { Required } from './Required';

interface DataAttributes {
  'data-tooltip-id'?: string;
  'data-tooltip-content'?: string;
  'data-tooltip-place'?: string;
}

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  className?: string;
  fontSize?: string;
  IconEnd?: IconType;
  IconStart?: IconType;
  ButtonStart?: React.ReactNode;
  ButtonEnd?: React.ReactNode;
  small?: boolean;
  width?: string;
  tooltipConfig?: DataAttributes;
  inputType?: 'SELECT' | 'INPUT';
  label?: string | React.ReactNode;
  classNameLabel?: string;
  showRequired?: boolean;
  someDescription?: React.ReactNode;
  error?: string | string[] | null;
  containerClassnames?: string;
  errorTooltip?: string | string[] | null;
}

const BASE_INPUT_CLASSES =
  'block bg-white m-0 w-full overflow-hidden text-slate-700 font-normal rounded px-3 py-1 border border-gray-300 placeholder:text-gray-400 placeholder:font-normal focus:border-blue-400 sm:leading-6 outline-none placeholder:transition-all focus:placeholder:opacity-60 placeholder:text-xs bg-transparent dark:bg-transparent dark:border-zinc-600 focus:placeholder:translate-x-1 dark:text-zinc-400 disabled:bg-gray-100 disabled:dark:bg-zinc-950 dark:disabled:text-gray-400';

const ICON_BASE_CLASSES = 'text-gray-400 absolute top-0 h-full !text-lg pointer-events-none z-10';

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      fontSize = 'text-sm',
      IconEnd,
      IconStart,
      ButtonStart,
      ButtonEnd,
      type = 'text',
      width,
      tooltipConfig = {},
      inputType = 'INPUT',
      onKeyDown,
      small,
      label,
      classNameLabel,
      showRequired,
      someDescription,
      error,
      containerClassnames = '',
      errorTooltip,
      ...props
    },
    ref,
  ) => {
    const paddingClasses = clsx({
      'ps-7': !!IconStart && !small,
      'pe-7': !!IconEnd && !small,
      '!ps-5': !!IconStart && small,
      '!pe-5': !!IconEnd && small,
    });

    const borderClasses = clsx({
      'rounded-l-none !border-l-0': !!ButtonStart,
      'rounded-r-none !border-r-0': !!ButtonEnd,
    });

    const inputBaseClass = clsx(
      inputType === 'SELECT' ? 'read-only:cursor-pointer' : 'read-only:cursor-default',
      { 'border-red-500': !!error || !!errorTooltip },
      BASE_INPUT_CLASSES,
      paddingClasses,
      borderClasses,
      className,
    );

    const containerClasses = clsx(
      'relative flex h-full',
      small && 'py-0',
      !!label && 'flex-col gap-0',
      containerClassnames,
    );
    const wrapperClasses = clsx('relative w-full h-full', small && 'py-0');

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
      }
      onKeyDown?.(e);
    };

    const mergedProps = {
      ...props,
      ...tooltipConfig,
      ...(errorTooltip && {
        'data-tooltip-id': 't4tms-tooltip',
        'data-tooltip-place': 'top',
        'data-tooltip-content': errorTooltip,
      }),
    } as React.InputHTMLAttributes<HTMLInputElement>;

    return (
      <div className={containerClasses} style={width ? { width } : undefined}>
        {!!label && (
          <label htmlFor={props.id} className={clsx('text-xs', classNameLabel)}>
            {label} {showRequired && <Required />}{' '}
            {!!someDescription && <span className="text-xs text-slate-400">{someDescription}</span>}
          </label>
        )}
        <div className="flex h-full w-full">
          {ButtonStart && ButtonStart}
          <div className={clsx(wrapperClasses, type === 'date' && 'relative')}>
            {IconStart && (
              <IconStart
                size={12}
                className={clsx(ICON_BASE_CLASSES, small ? 'left-1' : 'left-2.5')}
              />
            )}
            <input
              className={clsx(inputBaseClass, fontSize, { '!px-2.5 !py-0': small })}
              onKeyDown={handleKeyDown}
              type={type}
              ref={ref}
              {...mergedProps}
            />
            {IconEnd && (
              <IconEnd
                size={12}
                className={clsx(ICON_BASE_CLASSES, small ? 'right-1' : 'right-2.5')}
              />
            )}
          </div>
          {ButtonEnd && ButtonEnd}
        </div>
        {!!error && <small className="w-100 block text-xs italic text-red-500">{error}</small>}
      </div>
    );
  },
);

Input.displayName = 'Input';
